import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { config } from '@/lib/config'
import { getMsalInstanceForApi } from '@/contexts/AuthContext'
import { apiConfig } from '@/config/auth'

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: config.api.baseUrl,
  timeout: config.api.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Token acquisition helper
async function getAccessToken(): Promise<string | null> {
  const msalInstance = getMsalInstanceForApi()
  if (!msalInstance) return null

  const accounts = msalInstance.getAllAccounts()
  if (accounts.length === 0) return null

  try {
    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: [apiConfig.backendApiScope],
      account: accounts[0],
    })
    return tokenResponse.accessToken
  } catch (error) {
    console.error('Token acquisition failed:', error)
    try {
      const tokenResponse = await msalInstance.acquireTokenPopup({
        scopes: [apiConfig.backendApiScope],
        account: accounts[0],
      })
      return tokenResponse.accessToken
    } catch (popupError) {
      console.error('Popup token acquisition failed:', popupError)
      return null
    }
  }
}

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = await getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      // Server responded with error
      const status = error.response.status
      const data = error.response.data as { error?: { message?: string } }

      if (status === 401) {
        console.error('Unauthorized - redirecting to login')
        // Could trigger logout here
      } else if (status === 403) {
        console.error('Forbidden - insufficient permissions')
      } else if (status === 404) {
        console.error('Resource not found')
      } else if (status >= 500) {
        console.error('Server error:', data?.error?.message || 'Unknown error')
      }

      // Enhance error message
      error.message = data?.error?.message || error.message
    } else if (error.request) {
      // Request made but no response
      error.message = 'Network error - please check your connection'
    }

    return Promise.reject(error)
  }
)

export default apiClient
